import { Segmented } from "antd";
import { useCallback, useEffect, useState } from "react";

export const DATEBLOCK_VIEW_MODE_STORAGE_KEY = "ciq_dateblock_view_mode";

/** Tracks which project the current view-mode preference belongs to. */
export const DATEBLOCK_VIEW_MODE_PROJECT_KEY =
  "ciq_dateblock_view_mode_project";

const DATEBLOCK_VIEW_MODE_CHANGE_EVENT = "ciq:dateblock-view-mode-change";

export type DateblockViewMode = "classic" | "new";

/** Default when nothing is stored in sessionStorage (Full View / classic). */
export const DEFAULT_DATEBLOCK_VIEW_MODE: DateblockViewMode = "classic";

const CLASSIC_LABEL = "Full View";
const NEW_INTERFACE_LABEL = "Focused View";

const VALUE_TO_MODE: Record<string, DateblockViewMode> = {
  [CLASSIC_LABEL]: "classic",
  [NEW_INTERFACE_LABEL]: "new"
};

const MODE_TO_VALUE: Record<DateblockViewMode, string> = {
  classic: CLASSIC_LABEL,
  new: NEW_INTERFACE_LABEL
};

export function readDateblockViewMode(): DateblockViewMode {
  try {
    const v = sessionStorage.getItem(DATEBLOCK_VIEW_MODE_STORAGE_KEY);
    return v === "new" ? "new" : DEFAULT_DATEBLOCK_VIEW_MODE;
  } catch {
    return DEFAULT_DATEBLOCK_VIEW_MODE;
  }
}

function writeDateblockViewMode(mode: DateblockViewMode) {
  try {
    sessionStorage.setItem(DATEBLOCK_VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore quota / private mode */
  }
  try {
    globalThis.dispatchEvent(
      new CustomEvent(DATEBLOCK_VIEW_MODE_CHANGE_EVENT, { detail: mode })
    );
  } catch {
    /* SSR or restricted environment */
  }
}

/**
 * Resets dateblock view mode to classic (Full View) when the active project
 * changes. Preference is kept across navigations within the same project.
 */
export function syncDateblockViewModeForProject(projectId: string) {
  if (!projectId) return;
  try {
    const previousProjectId = sessionStorage.getItem(
      DATEBLOCK_VIEW_MODE_PROJECT_KEY
    );
    if (previousProjectId !== null && previousProjectId !== projectId) {
      writeDateblockViewMode(DEFAULT_DATEBLOCK_VIEW_MODE);
    }
    sessionStorage.setItem(DATEBLOCK_VIEW_MODE_PROJECT_KEY, projectId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function useDateblockViewMode(): DateblockViewMode {
  const [mode, setMode] = useState<DateblockViewMode>(readDateblockViewMode);

  useEffect(() => {
    const handleCustomEvent = (e: Event) => {
      const next = (e as CustomEvent<DateblockViewMode>).detail;
      if (next === "classic" || next === "new") {
        setMode(next);
      } else {
        setMode(readDateblockViewMode());
      }
    };

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === DATEBLOCK_VIEW_MODE_STORAGE_KEY) {
        setMode(readDateblockViewMode());
      }
    };

    globalThis.addEventListener(
      DATEBLOCK_VIEW_MODE_CHANGE_EVENT,
      handleCustomEvent
    );
    globalThis.addEventListener("storage", handleStorageEvent);
    return () => {
      globalThis.removeEventListener(
        DATEBLOCK_VIEW_MODE_CHANGE_EVENT,
        handleCustomEvent
      );
      globalThis.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  return mode;
}

function DateblockViewModeToggle(
  props: Readonly<{ disabled?: boolean; className?: string }>
) {
  const { disabled, className } = props;
  const mode = useDateblockViewMode();

  const handleChange = useCallback((value: string | number) => {
    const next = VALUE_TO_MODE[value as string];
    if (!next) return;
    writeDateblockViewMode(next);
  }, []);

  return (
    <Segmented
      className={className}
      data-testid="dateblock-view-mode-toggle"
      disabled={disabled}
      value={MODE_TO_VALUE[mode]}
      onChange={handleChange}
      options={[CLASSIC_LABEL, NEW_INTERFACE_LABEL]}
    />
  );
}

export default DateblockViewModeToggle;
